import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import sanitizeHtml from '../../utils/sanitizeHtml';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './CadastroModulos.css';

const CadastroModulos = () => {
  const { cursoId } = useParams();
  const [curso, setCurso] = useState(null);
  const [modulos, setModulos] = useState([]);
  const [novoModulo, setNovoModulo] = useState({
    title: '',
    content: ''
  });
  const [loading, setLoading] = useState(false);
  const [loadingCurso, setLoadingCurso] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingIndex, setEditingIndex] = useState(-1);
  const navigate = useNavigate();

  // Configuração do editor Quill
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['blockquote', 'code-block'],
      [{ 'color': [] }, { 'background': [] }],
      ['clean']
    ]
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'align',
    'link', 'image',
    'blockquote', 'code-block',
    'color', 'background'
  ];

  useEffect(() => {
    fetchCurso();
    fetchModulos();
  }, [cursoId]);

  const fetchCurso = async () => {
    try {
      const response = await apiService.cursos.get(cursoId);
      setCurso(response.data);
    } catch (err) {

      setError('Erro ao carregar dados do curso');
    } finally {
      setLoadingCurso(false);
    }
  };

  const fetchModulos = async () => {
    try {
      const response = await apiService.modulos.listByCourse(cursoId);
      setModulos(response.data);
    } catch (err) {

      // Se não há módulos ainda, não é erro
      setModulos([]);
    }
  };

  const handleModuloChange = (e) => {
    const { name, value } = e.target;
    setNovoModulo(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpar mensagens quando o usuário começar a digitar
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  // Função específica para mudanças no editor Quill
  const handleContentChange = useCallback((content) => {
    setNovoModulo(prev => {
      // Evitar rerender se o conteúdo for o mesmo
      if (prev.content === content) return prev;
      
      return {
        ...prev,
        content: content
      };
    });
    
    // Limpar mensagens quando o usuário começar a digitar
    if (error) setError(null);
    if (success) setSuccess(null);
  }, [error, success]);

  const handleAddModulo = async (e) => {
    e.preventDefault();
    
    // Validar se o conteúdo tem texto (remover HTML tags para validação)
    const contentText = novoModulo.content.replace(/<[^>]*>/g, '').trim();
    
    if (!novoModulo.title.trim() || !contentText) {
      setError('Título e conteúdo são obrigatórios');
      setSuccess(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      if (editingIndex >= 0) {
        // Editando módulo existente
        const moduloId = modulos[editingIndex].id;
        await apiService.modulos.update(cursoId, moduloId, novoModulo);
        
        const updatedModulos = [...modulos];
        updatedModulos[editingIndex] = { ...updatedModulos[editingIndex], ...novoModulo };
        setModulos(updatedModulos);
        setEditingIndex(-1);
        setSuccess('Módulo atualizado com sucesso!');
      } else {
        // Criando novo módulo
        const response = await apiService.modulos.create(cursoId, novoModulo);
        setModulos(prev => [...prev, response.data]);
        setSuccess('Módulo adicionado com sucesso!');
      }
      
      setNovoModulo({ title: '', content: '' });
      
      // Limpar mensagem de sucesso após 3 segundos
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
      
    } catch (err) {

      setError('Erro ao salvar módulo. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditModulo = (index) => {
    const modulo = modulos[index];
    setNovoModulo({
      title: modulo.title,
      content: modulo.content
    });
    setEditingIndex(index);
    setError(null);
    setSuccess(null);
  };

  const handleDeleteModulo = async (index) => {
    if (!window.confirm('Tem certeza que deseja excluir este módulo?')) {
      return;
    }

    try {
      const moduloId = modulos[index].id;
      await apiService.modulos.delete(cursoId, moduloId);
      
      const updatedModulos = modulos.filter((_, i) => i !== index);
      setModulos(updatedModulos);
      setSuccess('Módulo excluído com sucesso!');
      setError(null);
      
      // Limpar mensagem de sucesso após 3 segundos
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
      
    } catch (err) {

      setError('Erro ao excluir módulo. Tente novamente.');
      setSuccess(null);
    }
  };

  const handleCancelEdit = () => {
    setNovoModulo({ title: '', content: '' });
    setEditingIndex(-1);
    setError(null);
    setSuccess(null);
  };

  const handleFinish = () => {
    setSuccess('Curso finalizado com sucesso! Redirecionando...');
    setTimeout(() => {
      navigate('/admin/cursos/novo');
    }, 2000);
  };

  if (loadingCurso) {
    return (
      <div className="cadastro-modulos-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Carregando curso...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cadastro-modulos-container" data-page="cadastro-modulos">
      {/* Notificação Global */}
      {(success || error) && (
        <div className={`global-notification ${success ? 'success' : 'error'}`}>
          <div className="notification-content">
            <span className="notification-icon">
              {success ? '✅' : '⚠️'}
            </span>
            <span className="notification-message">
              {success || error}
            </span>
          </div>
        </div>
      )}

      <div className="modulos-header">
        <div className="curso-info">
          <h1>Módulos do Curso</h1>
          <h2>{curso?.title}</h2>
          <p>{curso?.description}</p>
        </div>
        <div className="admin-nav">
          <div className="progress-info">
            <span className="modulos-count">{modulos.length} módulos cadastrados</span>
          </div>
          <div className="admin-actions">
            <button
              onClick={() => navigate(`/admin/cursos/${cursoId}/exames`)}
              className="btn-admin-nav"
              title="Gerenciar provas e questões"
            >
              📋 Gestão de Provas
            </button>
          </div>
        </div>
      </div>

      <div className="modulos-content">
        {/* Formulário de Módulo */}
        <div className="modulo-form-section">
          <h3>{editingIndex >= 0 ? 'Editar Módulo' : 'Adicionar Novo Módulo'}</h3>
          
          <form onSubmit={handleAddModulo} className="modulo-form">
            {error && (
              <div className="error-alert">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}

            {success && (
              <div className="success-alert">
                <span className="success-icon">✅</span>
                {success}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="title">Título do Módulo *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={novoModulo.title}
                onChange={handleModuloChange}
                placeholder="Ex: Introdução ao curso"
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="content">Conteúdo do Módulo *</label>
              <div className="editor-container">
                <ReactQuill
                  theme="snow"
                  value={novoModulo.content || ''}
                  onChange={handleContentChange}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Digite o conteúdo do módulo..."
                  className="custom-quill-editor"
                  preserveWhitespace={false}
                  bounds={'.editor-container'}
                />
              </div>
              <div className="editor-help">
                <small>
                  💡 <strong>Dicas:</strong> Use cabeçalhos para organizar o conteúdo, 
                  listas para pontos importantes, e formatação para destacar informações relevantes.
                </small>
              </div>
            </div>

            <div className="form-actions">
              {editingIndex >= 0 && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="btn-cancel"
                >
                  Cancelar Edição
                </button>
              )}
              
              <button
                type="submit"
                className="btn-primary"
                disabled={loading}
                style={{ marginTop: '2rem' }}
              >
                {loading ? (
                  <>
                    <span className="loading-spinner"></span>
                    Salvando...
                  </>
                ) : editingIndex >= 0 ? (
                  <>
                    <span className="btn-icon">✏️</span>
                    Atualizar Módulo
                  </>
                ) : (
                  <>
                    <span className="btn-icon">➕</span>
                    Adicionar Módulo
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Lista de Módulos */}
        <div className="modulos-list-section">
          <h3>Módulos Cadastrados</h3>
          
          {modulos.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📚</div>
              <h4>Nenhum módulo cadastrado</h4>
              <p>Adicione o primeiro módulo para começar</p>
            </div>
          ) : (
            <div className="modulos-grid">
              {modulos.map((modulo, index) => (
                <div key={modulo.id} className={`modulo-card ${editingIndex === index ? 'editing' : ''}`}>
                  <div className="modulo-header">
                    <div className="modulo-number">#{index + 1}</div>
                    <div className="modulo-actions">
                      <button
                        onClick={() => handleEditModulo(index)}
                        className="action-btn edit-btn"
                        title="Editar módulo"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteModulo(index)}
                        className="action-btn delete-btn"
                        title="Excluir módulo"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                  
                  <h4 className="modulo-title">{modulo.title}</h4>
                  <div className="modulo-preview">
                    <div 
                      dangerouslySetInnerHTML={{ 
                        __html: sanitizeHtml(modulo.content.length > 150
                          ? `${modulo.content.substring(0, 150)}...`
                          : modulo.content)
                      }}
                    />
                  </div>
                  
                  <div className="modulo-footer">
                    <span className="content-length">
                      {modulo.content.replace(/<[^>]*>/g, '').length} caracteres
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Actions Bottom */}
      <div className="bottom-actions">
        <button
          onClick={() => navigate('/admin/cursos/novo')}
          className="btn-secondary"
        >
          Voltar aos Cursos
        </button>
        
        <button
          onClick={handleFinish}
          className="btn-success"
          disabled={modulos.length === 0}
        >
          <span className="btn-icon">✅</span>
          Finalizar Curso
        </button>
      </div>
    </div>
  );
};

export default CadastroModulos;
